import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { EditSheet, DangerAction, changedFields, toField } from '../ui/EditSheet';
import { Field, field } from '../create/Field';
import { Skeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  ForbiddenState,
  OfflineState,
  isForbidden,
  queryState,
} from '../ui/QueryStates';
import { IconCustomers } from '../icons';

interface FullProperty {
  id: string;
  label: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  accessNotes: string | null;
  gateCode: string | null;
}

/**
 * Fix an address.
 *
 * ⚠️ THE ROW IS FETCHED HERE, not handed down from the page, and that is an access-control
 * decision rather than a data-flow preference. Neither of the queries the page runs carries
 * `gateCode` or `accessNotes` any more: `customer.get` is `customer:read` (a bookkeeper holds
 * it) and `customer.properties` is `property:read` (a TECH holds it, for every address in the
 * shop). This form is the one place in the app whose job is to show those two values, so it
 * asks the only door scoped to that job — `property:update`.
 *
 * Prefilling from the page's copy instead is worse than it looks: an empty gate-code box on a
 * property that HAS a gate code is a screen telling somebody there is no code, and the next
 * person to drive out there believes it.
 *
 * The gate code and the access notes get the same weight here as the street address rather
 * than being buried at the bottom — whoever is correcting them is correcting them because
 * somebody already made a wasted drive.
 */
export function EditPropertySheet({
  open,
  onClose,
  propertyId,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: string;
}) {
  const trpc = useTRPC();
  // `propertyForEdit`, not `properties`. The list is `property:read` and no longer carries
  // the gate code at all; this one is `property:update` and exists to carry it. It is also
  // its OWN query key, which matters more than it looks: `CustomerEquipmentCard` sits behind
  // this sheet observing the list, and a shared key meant that card's focus-refetch could
  // move this form's prefill mid-edit. `staleTime` would not have stopped it — that setting
  // is per-observer, and the other observer's defaults win.
  const q = useQuery(trpc.customer.propertyForEdit.queryOptions({ propertyId }));
  const property = q.data;

  // The sheet opens on the click, not on the response — a modal that appears a beat after
  // the tap reads as a missed tap. So the shell renders immediately and the BODY carries
  // the state, four ways, in `queryState`'s order.
  //
  // ⚠️ `if (!property)` alone was wrong here, and wrong in the way this repo has a named law
  // against: it folded offline, error, forbidden and "no such row" into one skeleton. On a
  // phone with no signal that skeleton never resolves, and if the address was archived in
  // another tab it never resolves either — with nothing on screen saying why.
  if (!property) {
    const state = queryState(q);
    return (
      <EditSheet
        open={open}
        onClose={onClose}
        title="Edit address"
        dirty={false}
        onSave={() => {}}
      >
        {state === 'offline' ? (
          <OfflineState icon={IconCustomers} what="this address" />
        ) : state === 'loading' ? (
          <>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </>
        ) : state === 'error' ? (
          // A 403 is a permanent answer. Offering "Try again" under it is a button that can
          // only fail — the same tell the tech pages were fixed for.
          isForbidden(q.error) ? (
            <ForbiddenState icon={IconCustomers} what="this address" />
          ) : (
            <ErrorState
              icon={IconCustomers}
              what="this address"
              onRetry={() => void q.refetch()}
            />
          )
        ) : (
          // Loaded, and the row is not in it: archived or deleted while the list on the page
          // behind was still showing it. Nothing is broken and there is nothing to retry, so
          // no button — the sheet's own Cancel is the way out.
          <EmptyState
            icon={IconCustomers}
            title="That address is gone"
            description="It was retired or deleted. The list behind this will catch up when you close."
          />
        )}
      </EditSheet>
    );
  }
  return <PropertyForm open={open} onClose={onClose} property={property} />;
}

function PropertyForm({
  open,
  onClose,
  property,
}: {
  open: boolean;
  onClose: () => void;
  property: FullProperty;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();

  // ⚠️ `useState`, not a plain object recomputed each render — `original` is the BASELINE
  // that `changedFields` diffs against, so it has to be the row as it was when this form
  // opened. Recomputed, a refetch that lands mid-edit moves `original` while `draft` stays
  // put, and every field the user did NOT touch starts reading as an edit: Save then writes
  // the stale gate code back over somebody else's correction. Captured once, at mount.
  const [original] = useState(() => ({
    label: property.label,
    address1: toField(property.address1),
    address2: toField(property.address2),
    city: toField(property.city),
    state: toField(property.state),
    zip: toField(property.zip),
    accessNotes: toField(property.accessNotes),
    gateCode: toField(property.gateCode),
  }));
  const [draft, setDraft] = useState(original);
  const set = (k: keyof typeof original) => (v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const changed = changedFields(original, draft);
  const dirty = Object.keys(changed).length > 0;

  const done = () => {
    void qc.invalidateQueries();
    onClose();
  };
  const save = useMutation(
    trpc.customer.updateProperty.mutationOptions({
      onSuccess: (row) => {
        // ⚠️ Write the saved row into THIS query's cache before closing.
        //
        // `invalidateQueries` in `done()` marks it stale; it does not wait for the refetch,
        // and the sheet unmounts immediately after. Successful queries are persisted for 24
        // hours, so reopening the same address a moment later mounts the form from the
        // PRE-SAVE row and freezes it as `original` — the screen then shows the gate code
        // somebody just corrected away, and the person reads it out. `changedFields` stops
        // that stale value being written back, but not from being believed.
        qc.setQueryData(
          trpc.customer.propertyForEdit.queryKey({ propertyId: property.id }),
          row,
        );
        done();
      },
    }),
  );
  const archive = useMutation(
    trpc.customer.archiveProperty.mutationOptions({
      onSuccess: () => {
        // Same window as the save path, one state further on: `invalidateQueries` leaves the
        // retired row readable until a refetch lands, and the list behind can still be
        // showing its Edit button for that beat. Reopening in that window would mount the
        // form on an address that no longer exists — gate code and all. `null` is what the
        // procedure will return once it is asked, so say it now.
        qc.setQueryData(
          trpc.customer.propertyForEdit.queryKey({ propertyId: property.id }),
          null,
        );
        done();
      },
    }),
  );

  return (
    <EditSheet
      open={open}
      onClose={onClose}
      title={`Edit ${property.label}`}
      dirty={dirty}
      saving={save.isPending}
      error={save.error ?? archive.error}
      onSave={() => save.mutate({ propertyId: property.id, ...changed, ...idem() })}
      destructive={
        <DangerAction
          label="Retire this address"
          question={`Retire ${property.label}?`}
          detail="It stops appearing when you book work. Visits that already happened there stay on the record — that work really did happen at this address."
          pending={archive.isPending}
          onConfirm={() => archive.mutate({ id: property.id, ...idem() })}
        />
      }
    >
      <Field label="What you call it">
        <input
          className={field}
          value={draft.label}
          onChange={(e) => set('label')(e.target.value)}
          autoFocus
        />
      </Field>
      <Field label="Street">
        <input
          className={field}
          value={draft.address1}
          onChange={(e) => set('address1')(e.target.value)}
        />
      </Field>
      <Field label="Unit / suite">
        <input
          className={field}
          value={draft.address2}
          onChange={(e) => set('address2')(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-3 gap-2">
        <Field label="City">
          <input
            className={field}
            value={draft.city}
            onChange={(e) => set('city')(e.target.value)}
          />
        </Field>
        <Field label="State">
          <input
            className={field}
            value={draft.state}
            onChange={(e) => set('state')(e.target.value.toUpperCase())}
            maxLength={2}
          />
        </Field>
        <Field label="ZIP">
          <input
            className={field}
            value={draft.zip}
            onChange={(e) => set('zip')(e.target.value)}
            inputMode="numeric"
          />
        </Field>
      </div>
      <Field label="Gate code">
        <input
          className={field}
          value={draft.gateCode}
          onChange={(e) => set('gateCode')(e.target.value)}
        />
      </Field>
      <Field label="Getting in / what to know">
        <textarea
          className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
          value={draft.accessNotes}
          onChange={(e) => set('accessNotes')(e.target.value)}
          placeholder="Dog in the back yard. Unit is on the roof, ladder access through the stockroom."
        />
      </Field>
      <p className="text-xs text-muted-foreground">
        Emptying a box clears it. Anything you don't touch stays as it is.
      </p>
    </EditSheet>
  );
}
